import React, { useState, useRef, useEffect } from "react";
import ReactModal from "react-modal";
import "../popups.css";
import axiosInstance from "../Middleware/interceptor";
import {
  getFamilyTreeHeadLineRestriction,
  isTreeSideApiValue,
} from "../Utils/subscriptionAccess";
import FamilyLineSideHeadDropdown from "./FamilyLineSideHeadDropdown";

if (process.env.NODE_ENV !== "test") {
    ReactModal.setAppElement("#root");
}

const SAMPLE_FILE_VIEWING_URL =
    "https://fam-connec.s3.us-east-1.amazonaws.com/Sample+File_For+Viewing+Only.xlsx";
const SAMPLE_FILE_VIEWING_FILENAME = "Sample File_For Viewing Only.xlsx";
const WORKSHEET_INSTRUCTIONS_URL =
    "https://fam-connec.s3.us-east-1.amazonaws.com/Worksheet_Import+Instructions+(2).pdf";
const WORKSHEET_INSTRUCTIONS_FILENAME = "Worksheet_Import Instructions (2).pdf";

const getFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) return "";
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim());
        } catch {
            return utf8Match[1].trim();
        }
    }
    const quotedMatch = /filename="([^"]+)"/i.exec(contentDisposition);
    if (quotedMatch?.[1]) return quotedMatch[1].trim();
    const plainMatch = /filename=([^;]+)/i.exec(contentDisposition);
    return plainMatch?.[1]?.trim().replace(/^"|"$/g, "") || "";
};

/** Backend often returns HTML Excel (.xls) with a misleading .csv Content-Disposition. */
const resolveSpreadsheetDownload = async (rawBlob, contentType, contentDisposition, isMemberExport) => {
    const baseName = isMemberExport ? "family_member_data" : "family_members_sample";
    const headerFilename = getFilenameFromContentDisposition(contentDisposition);
    const normalizedType = contentType?.split(";")[0]?.trim().toLowerCase() || "";

    const zipHead = new Uint8Array(await rawBlob.slice(0, 2).arrayBuffer());
    if (zipHead[0] === 0x50 && zipHead[1] === 0x4b) {
        const filename =
            headerFilename && /\.xlsx$/i.test(headerFilename)
                ? headerFilename
                : `${baseName}.xlsx`;
        return {
            blob: new Blob([rawBlob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }),
            filename,
        };
    }

    const snippet = await rawBlob.slice(0, 4096).text();
    const isExcelHtml =
        snippet.includes("schemas-microsoft-com:office:excel") ||
        /^\s*<(?:!DOCTYPE\s+html|html)/i.test(snippet);
    const isLegacyExcel =
        isExcelHtml ||
        normalizedType.includes("ms-excel") ||
        (normalizedType.includes("spreadsheet") && !normalizedType.includes("csv"));

    if (isLegacyExcel) {
        const filename =
            headerFilename && /\.xls$/i.test(headerFilename) && !/\.csv$/i.test(headerFilename)
                ? headerFilename
                : `${baseName}.xls`;
        return {
            blob: new Blob([rawBlob], { type: "application/vnd.ms-excel" }),
            filename,
        };
    }

    const filename =
        headerFilename && /\.csv$/i.test(headerFilename)
            ? headerFilename
            : `${baseName}.csv`;
    return {
        blob: new Blob([rawBlob], { type: "text/csv;charset=utf-8" }),
        filename,
    };
};

const ImportDataModal = ({
    isOpen = true,
    onClose,
    onImport,
    data,
    treeSide = "",
    onTreeSideChange,
    selectionLocked = false,
    visibleSides = "both",
}) => {
    const [fileName, setFileName] = useState("");
    const [selectedFile,setSelectedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef();
    const headLineRestriction = getFamilyTreeHeadLineRestriction();
    /** Both-line plans: require an API tree_side before import. Single-line plans pre-set side in familyTree. */
    const bothPlanNeedsSide =
        headLineRestriction === "both" && !isTreeSideApiValue(treeSide);

    useEffect(() => {
        if (isOpen) {
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        if (file) setFileName(file.name);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setSelectedFile(file);
        if (file) setFileName(file.name);
    };

    const handleDragOver = (e) => e.preventDefault();

    const triggerFileDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const downloadFileFromUrl = async (url, filename) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }
            const blob = await response.blob();
            triggerFileDownload(blob, filename);
        } catch (error) {
            console.error("Failed to download file:", error);
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const downloadSpreadsheetFromEndpoint = async (apiEndpoint, isMemberExport) => {
        try {
            const response = await axiosInstance.get(apiEndpoint, { responseType: "blob" });

            const rawBlob =
                response.data instanceof Blob ? response.data : new Blob([response.data]);
            const contentType = response.headers?.["content-type"] || "";
            const { blob, filename } = await resolveSpreadsheetDownload(
                rawBlob,
                contentType,
                response.headers?.["content-disposition"],
                isMemberExport
            );

            triggerFileDownload(blob, filename);
        } catch (error) {
            console.error("Failed to download file:", error);
        }
    };

    const handleDownloadSample = () =>
        downloadFileFromUrl(SAMPLE_FILE_VIEWING_URL, SAMPLE_FILE_VIEWING_FILENAME);

    const handleDownloadInputData = () => {
        const isMemberExport = Boolean(data);
        const apiEndpoint = isMemberExport
            ? `/download-family-member-csv/${data}`
            : "/download-sample-file";
        downloadSpreadsheetFromEndpoint(apiEndpoint, isMemberExport);
    };

    const handleDownloadWorksheetInstructions = () =>
        downloadFileFromUrl(WORKSHEET_INSTRUCTIONS_URL, WORKSHEET_INSTRUCTIONS_FILENAME);

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            style={{
                overlay: {
                    backgroundColor: "transparent",
                    zIndex: 1000,
                },
                content: {
                    inset: "auto",
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    overflow: "visible",
                },
            }}
        >
            <div id="importdata-popup">
                <div className="importdata-card">
                    <div className="importdata-header">
                        <h2>Import Data</h2>
                        <button className="close-btn" onClick={onClose}>
                            &times;
                        </button>
                    </div>

                    <div className="importdata-info">
                        <p>
                            Upload your family data making sure you use the same named fields
                            for easy importing. The data should be reviewed for accuracy and updated where needed.
                        </p>

                    </div>

                    {typeof onTreeSideChange === "function" && (
                        <div className="importdata-family-line-picker">
                            <FamilyLineSideHeadDropdown
                                value={treeSide}
                                onChange={onTreeSideChange}
                                headLineRestriction={headLineRestriction}
                                selectionLocked={selectionLocked}
                                visibleSides={visibleSides}
                                label=""
                                id="importFileFamilyLineSideHead"
                                listboxId="import-file-family-line-side-head-listbox"
                            />
                        </div>
                    )}

                    <div
                        className="dropzone"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current.click()}
                    >
                        <input
                            type="file"
                            accept=".csv,.xls,.xlsx"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            hidden
                        />
                        <div className="upload-content">
                            <img src={window.getPublicUrl("/assets/images/upload-icon.svg")} alt="user" />
                            {!fileName ? (
                                <>
                                    <p>
                                        Drag & Drop your file here
                                        <br />
                                        <span className="browse-link">or Click to Browse</span>
                                    </p>
                                    <small>Supported files, CSV & XLSX</small>
                                </>
                            ) : (
                                <p className="file-name">{fileName}</p>
                            )}
                        </div>
                    </div>
                    <div className="importdata-sample-section">
                        <div className="importdata-sample-links">
                            <button type="button" className="sample-btn" onClick={handleDownloadSample}>
                                Download Sample File (for viewing only)
                            </button>
                            <button type="button" className="sample-btn" onClick={handleDownloadInputData}>
                                Download Input Data File (for importing to app)
                            </button>
                            <button
                                type="button"
                                className="sample-btn"
                                onClick={handleDownloadWorksheetInstructions}
                            >
                                Worksheet & Import Instructions
                            </button>
                        </div>
                        <div className="importdata-info" style={{ flexDirection: 'column', alignItems: 'flex-start', marginTop: '15px' }}>
                            {/* Row and Parent Row Rule — hidden for now
                            <p style={{ marginBottom: '12px' }}>
                                <strong>Row and Parent Row Rule:</strong><br />
                                The row is the number given to the person and parent row tells us who the parent is. For example, if a person in row 3 has parent row 1, it means the person in row 1 is their parent.
                            </p>
                            */}
                            <p style={{ marginBottom: 0 }}>
                                <strong>Note:</strong> Images/Photos must be added to the app manually.<br />
                                Once the family color is assigned, it's carried down to subsequent generations in your line, automatically.
                            </p>
                        </div>
                    </div>


                    <div className="importdata-actions">
                        <button className="import-cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="import-confirm-btn"
                            disabled={isSubmitting || bothPlanNeedsSide}
                            style={{
                                opacity: isSubmitting || bothPlanNeedsSide ? 0.55 : 1,
                                cursor: isSubmitting || bothPlanNeedsSide ? "not-allowed" : "pointer",
                            }}
                            onClick={() => {
                                if (isSubmitting || bothPlanNeedsSide) return;
                                setIsSubmitting(true);
                                onImport(selectedFile);
                            }}
                        >
                            Import Now
                        </button>
                    </div>
                </div>
            </div>
        </ReactModal>
    );
};

export default ImportDataModal;